import { useParams, useOutletContext, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath, canonicalSlug } from "@/lib/routes";
import { toLangSlug } from "@/lib/slug_map";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import { breadcrumbSchema } from "@/lib/schema";

export default function ProjectDetail() {
  const { slug: urlSlug } = useParams();
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const daSlug = canonicalSlug("project", urlSlug);
  const { data: project, isLoading } = useQuery({ queryKey: ["project", daSlug], queryFn: () => api.getProject(daSlug) });

  const canonicalUrlSlug = toLangSlug("project", daSlug, lang);
  if (canonicalUrlSlug && urlSlug !== canonicalUrlSlug) {
    return <Navigate to={buildPath("project", lang, { slug: daSlug })} replace />;
  }

  if (isLoading || !project) return <div className="px-6 md:px-20 py-32 text-muted-foreground">…</div>;
  const c = project[lang] || project.da;
  const ex = project.extra?.[lang];
  const path = buildPath("project", lang, { slug: daSlug });
  const role = lang === "da" ? project.role_da : project.role_en;

  return (
    <>
      <Seo routeKey="project" params={{ slug: daSlug }} lang={lang} title={c.meta_title} description={c.meta_description}
        jsonLd={[breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_projects"), path: buildPath("projects", lang) }, { name: c.title, path }])]} />
      <PageHero title={c.title} description={c.tagline}
        crumbs={[{ label: t("nav_home"), to: "home" }, { label: t("nav_projects"), to: "projects" }, { label: c.title }]} />

      <section className="px-6 md:px-12 lg:px-20 py-16">
        <div className="grid gap-14 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-12">
            <Reveal><p className="text-xl font-light leading-relaxed text-foreground/90">{c.intro}</p></Reveal>
            {(c.sections || []).map((sec, i) => (
              <Reveal key={i}>
                <div className="border-t border-border pt-8">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">{sec.h}</h2>
                  <p className="mt-4 text-muted-foreground font-light leading-relaxed">{sec.p}</p>
                </div>
              </Reveal>
            ))}
            {ex?.paras?.length > 0 && (
              <Reveal>
                <div className="space-y-4 border-t border-border pt-8">
                  {ex.paras.map((p, i) => (
                    <p key={i} className="text-muted-foreground font-light leading-relaxed">{p}</p>
                  ))}
                </div>
              </Reveal>
            )}
            {ex?.approach?.length > 0 && (
              <Reveal>
                <div className="border-t border-border pt-8">
                  <h2 className="mb-6 font-heading text-2xl md:text-3xl font-bold tracking-tight">
                    {lang === "da" ? "Tilgang" : "Approach"}
                  </h2>
                  <div className="space-y-5">
                    {ex.approach.map((s, i) => (
                      <div key={i} className="flex gap-4">
                        <span className="shrink-0 font-mono text-sm text-muted-foreground">0{i + 1}</span>
                        <div>
                          <h3 className="font-heading font-bold">{s.h}</h3>
                          <p className="mt-1 text-sm text-muted-foreground font-light leading-relaxed">{s.p}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>
          <div>
            <Reveal>
              <div className="sticky top-24 space-y-4">
              <div className="rounded-xl border border-border bg-card p-7">
                <div className="label-mono text-muted-foreground/60">{t("role")}</div>
                <div className="mt-2 font-heading text-lg font-bold">{role}</div>
                <div className="mt-5 label-mono text-muted-foreground/60">URL</div>
                <div className="mt-1 text-sm text-muted-foreground break-all">{project.external_url?.replace("https://", "")}</div>
                <a href={project.external_url} target="_blank" rel="noopener noreferrer" data-testid="project-visit"
                  className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3.5 font-medium text-primary-foreground transition-transform hover:scale-[1.03]">
                  {c.cta}
                  <ArrowUpRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
              {ex?.table && (
                <div className="rounded-xl border border-border bg-card p-7">
                  <div className="label-mono mb-3 text-muted-foreground/60">{ex.table.title}</div>
                  <dl className="space-y-3">
                    {ex.table.rows.map((row, i) => (
                      <div key={i} className="flex flex-col border-b border-border pb-3 last:border-0">
                        <dt className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/60">{row[0]}</dt>
                        <dd className="mt-0.5 text-sm">{row[1]}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <LeadMagnet profile={profile} />
    </>
  );
}
